import {
  MarketingTriggerKey,
  OrderTrackingTriggerKey,
  ReturnTrackingTriggerKey,
  type TriggerKey,
} from "@redotech/redo-flows-common/triggers";
import { SkillId } from "../skill-id";
import type { TriggerDefinition } from "../trigger-definition";
import {
  POST_PURCHASE_CATEGORIES,
  PRE_PURCHASE_CATEGORIES,
  TriggerCategory,
  TriggerConversationType,
} from "../trigger-definition";

// ---------------------------------------------------------------------------
// Unsupported triggers — intentionally NOT handled by the shopper agent.
//
// If you want to support a trigger, remove it from this map and add a
// TriggerDefinition below. The triggers.spec.ts test enforces that every
// TriggerKey is either defined here or listed as unsupported.
// ---------------------------------------------------------------------------

const unsupportedEntries: [TriggerKey, string][] = [
  // No product selection UI exists yet — merchants can't configure which
  // products to monitor, so enabling this would do nothing useful.
  [MarketingTriggerKey.LOW_INVENTORY, "No product selection UI exists yet"],

  // Campaign and date triggers are manual/scheduled sends, not event-driven.
  // They don't fit the shopper agent's reactive model.
  [
    MarketingTriggerKey.MARKETING_CAMPAIGN,
    "Manual/scheduled send, not event-driven",
  ],
  [MarketingTriggerKey.DATE, "Date-based scheduled send, not event-driven"],
  [
    MarketingTriggerKey.GIFT_CARD_BALANCE,
    "Scheduled balance scan, not event-driven",
  ],

  // Customer group triggers need segment evaluation infrastructure that
  // isn't wired into the marketing agent coordinator yet.
  [
    MarketingTriggerKey.CUSTOMER_GROUP_ENTERED,
    "Segment evaluation not wired to marketing agent",
  ],
  [
    MarketingTriggerKey.CUSTOMER_GROUP_EXITED,
    "Segment evaluation not wired to marketing agent",
  ],

  // Post-purchase upsell is a conversion product feature, not a messaging trigger.
  [
    OrderTrackingTriggerKey.POST_PURCHASE_UPSELL,
    "Conversion product feature, not a messaging trigger",
  ],

  // Replies reach the agent through support-v2 via the inbound webhook, gated by
  // shopperAgent.replyConfig; the key is a discriminator, not a configurable trigger.
  [
    MarketingTriggerKey.INBOUND_RECEIVED,
    "Stored by the inbound webhook and gated by shopperAgent.replyConfig, not configurable per-trigger",
  ],

  // Subscription cancellation is handled by a dedicated Recharge flow trigger,
  // not by the shopper-agent. Requires a Recharge integration to fire.
  [
    MarketingTriggerKey.PRODUCT_SUBSCRIPTION_CANCELLED,
    "Handled by Recharge subscription-cancelled flow, not shopper-agent",
  ],
  [
    ReturnTrackingTriggerKey.COVERAGE_REFUNDED,
    "Handled by coverage-refunded flow email, not shopper-agent",
  ],
];

export const UNSUPPORTED_TRIGGERS: ReadonlyMap<TriggerKey, string> = new Map(
  unsupportedEntries,
);

// ---------------------------------------------------------------------------
// Cart & Checkout
// ---------------------------------------------------------------------------

export const CHECKOUT_ABANDONED_OUTREACH_GUIDANCE =
  "The customer started checkout and left without paying, so no order was placed.";
export const CART_ABANDONED_OUTREACH_GUIDANCE =
  "The customer added items to their cart and left without starting checkout.";
export const BROWSE_ABANDONED_OUTREACH_GUIDANCE =
  "The customer viewed products and left without adding any to their cart.";

const CART_OR_CHECKOUT_ABANDONED_SEND_POLICY_GUIDANCE =
  "Wait until the shopping session ends (about 15 minutes with no activity), then send. Skip if they have since bought these items (another variant counts), or talked with us in the last 24 hours about these items or a problem checking out.";
const BROWSE_ABANDONED_SEND_POLICY_GUIDANCE =
  "Wait until the shopping session ends (about 15 minutes with no activity), then send. Skip if they have since bought what they viewed, already own it, have it on an open order, or are returning it, or talked with us about it in the last 24 hours.";

const checkoutAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.CHECKOUT_ABANDONED,
  name: "Checkout Abandoned",
  description: "Customer started checkout but didn't complete the purchase",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  // HSA is Shopify-only and stripped by the skill plan unless the cart
  // actually holds an eligible item; see marketing-agents' skill-plan.
  skills: [SkillId.DISCOUNT, SkillId.HSA],
  guidance: CHECKOUT_ABANDONED_OUTREACH_GUIDANCE,
  sendPolicyGuidance: CART_OR_CHECKOUT_ABANDONED_SEND_POLICY_GUIDANCE,
};

const cartAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.CART_ABANDONED,
  name: "Cart Abandoned",
  description: "Customer added items to cart but didn't proceed to checkout",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT, SkillId.HSA],
  guidance: CART_ABANDONED_OUTREACH_GUIDANCE,
  sendPolicyGuidance: CART_OR_CHECKOUT_ABANDONED_SEND_POLICY_GUIDANCE,
};

const browseAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.BROWSE_ABANDONED,
  name: "Browse Abandoned",
  description: "Customer browsed products but didn't add anything to cart",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance: BROWSE_ABANDONED_OUTREACH_GUIDANCE,
  sendPolicyGuidance: BROWSE_ABANDONED_SEND_POLICY_GUIDANCE,
};

const emailSignupTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.EMAIL_SIGNUP,
  name: "Email Signup",
  description: "Customer signed up for email marketing",
  category: TriggerCategory.SIGNUPS,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance: "The customer just subscribed to the brand's marketing emails.",
  sendPolicyGuidance:
    "Send now. Skip if we already welcomed them to email marketing.",
  processingPolicy: {
    buffer: { defaultBypass: true },
    sentinel: { defaultBypass: true },
  },
};

const smsSignupTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.SMS_SIGNUP,
  name: "SMS Signup",
  description: "Customer confirmed SMS marketing subscription",
  category: TriggerCategory.SIGNUPS,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  // SMS regulation requires that confirmation/welcome flows always fire —
  // the shopper agent bypass for SMS_SIGNUP is hardcoded in
  // marketing-agent-routing.ts. Disallow attaching automations here so the
  // merchant can't configure a state that would never be consulted.
  supportsAutomations: false,
  // Same reasoning — the coordinator handoff for SMS_SIGNUP happens in
  // MarketingConfirmedHandler with no SchemaInstance in scope, so skip
  // conditions can't be evaluated even if persisted. Lock the field to
  // prevent merchants from configuring something that would never apply.
  supportsSkipConditions: false,
  skills: [SkillId.DISCOUNT],
  guidance: "The customer just confirmed the brand's SMS marketing.",
  sendPolicyGuidance:
    "Send now. Skip if we already welcomed them to SMS marketing.",
  processingPolicy: {
    buffer: { defaultBypass: true },
    sentinel: { defaultBypass: true },
  },
};

const priceDropTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.PRICE_DROP,
  name: "Price Drop",
  description: "A product the customer showed interest in dropped in price",
  category: TriggerCategory.PRODUCT_INTEREST,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The price of a product the customer showed interest in (viewed, carted, or started checkout with) went down. They did not ask to be told.",
  sendPolicyGuidance:
    "Send now. Skip if they have since bought it (another variant counts) or returned it.",
};

const backInStockTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.BACK_IN_STOCK,
  name: "Back in Stock",
  description: "An out-of-stock product the customer wanted is available again",
  category: TriggerCategory.PRODUCT_INTEREST,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "A sold-out product the customer asked to be told about is available again.",
  sendPolicyGuidance:
    "Send now. Skip if they have since bought it (another variant counts).",
  processingPolicy: {
    buffer: { defaultBypass: true },
    sentinel: { defaultBypass: true },
  },
};

const emailSignupShopifyTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.EMAIL_SIGNUP_SHOPIFY,
  name: "Email Signup (Shopify)",
  description: "Customer signed up for email marketing via Shopify",
  category: TriggerCategory.SIGNUPS,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The customer subscribed to the brand's marketing emails through Shopify, often by ticking the marketing box at checkout while placing an order.",
  sendPolicyGuidance: emailSignupTrigger.sendPolicyGuidance,
  processingPolicy: emailSignupTrigger.processingPolicy,
};

const bigcommerceCartAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.BIGCOMMERCE_CART_ABANDONED,
  name: "Cart Abandoned (BigCommerce)",
  description:
    "BigCommerce customer added items to cart but didn't proceed to checkout",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance: CART_ABANDONED_OUTREACH_GUIDANCE,
  sendPolicyGuidance: CART_OR_CHECKOUT_ABANDONED_SEND_POLICY_GUIDANCE,
};

const bigcommerceCheckoutAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.BIGCOMMERCE_CHECKOUT_ABANDONED,
  name: "Checkout Abandoned (BigCommerce)",
  description:
    "BigCommerce customer started checkout but didn't complete the purchase",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance: CHECKOUT_ABANDONED_OUTREACH_GUIDANCE,
  sendPolicyGuidance: CART_OR_CHECKOUT_ABANDONED_SEND_POLICY_GUIDANCE,
};

const commentsoldCartAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.COMMENTSOLD_CART_ABANDONED,
  name: "Cart Abandoned (CommentSold)",
  description:
    "CommentSold customer added items to cart but didn't proceed to checkout",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance: CART_ABANDONED_OUTREACH_GUIDANCE,
  sendPolicyGuidance: CART_OR_CHECKOUT_ABANDONED_SEND_POLICY_GUIDANCE,
};

const commentsoldBrowseAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.COMMENTSOLD_BROWSE_ABANDONED,
  name: "Browse Abandoned (CommentSold)",
  description: "CommentSold customer browsed products but didn't add to cart",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance: BROWSE_ABANDONED_OUTREACH_GUIDANCE,
  sendPolicyGuidance: BROWSE_ABANDONED_SEND_POLICY_GUIDANCE,
};

const commentsoldCheckoutAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.COMMENTSOLD_CHECKOUT_ABANDONED,
  name: "Checkout Abandoned (CommentSold)",
  description:
    "CommentSold customer started checkout but didn't complete the purchase",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance: CHECKOUT_ABANDONED_OUTREACH_GUIDANCE,
  sendPolicyGuidance: CART_OR_CHECKOUT_ABANDONED_SEND_POLICY_GUIDANCE,
};

// ---------------------------------------------------------------------------
// Returns & Warranty
// ---------------------------------------------------------------------------

const refundReturnSubmittedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.REFUND_RETURN_SUBMITTED,
  name: "Refund Return Processed",
  description: "Customer's refund return has finished processing",
  category: TriggerCategory.RETURNS_WARRANTY,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The customer's return finished processing and was refunded, rather than exchanged for other items or store credit.",
  sendPolicyGuidance:
    "Send now. Skip if they have placed a new order since the refund.",
};

const exchangeProcessedWithCreditTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.EXCHANGE_PROCESSED_WITH_CREDIT,
  name: "Exchange Processed with Credit",
  description: "Customer has leftover store credit from an exchange",
  category: TriggerCategory.RETURNS_WARRANTY,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The customer's exchange was processed, and store credit was left over after paying for the new items. The new items ship on a separate exchange order (marked isExchangeOrder).",
  sendPolicyGuidance:
    "Send now. Skip if they have placed a new order since, other than the exchange order.",
};

const warrantyRegistrationTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.WARRANTY_REGISTRATION,
  name: "Warranty Registration",
  description: "Customer registered a product warranty",
  category: TriggerCategory.RETURNS_WARRANTY,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The customer registered a warranty for a product they own, possibly bought from another retailer.",
  sendPolicyGuidance:
    "Send now, once: skip if we messaged them about a warranty registration in the last 24 hours.",
};

// ---------------------------------------------------------------------------
// Orders & Shipping
// ---------------------------------------------------------------------------

const FIRST_SHIPPING_UPDATE_SEND_POLICY_GUIDANCE =
  "Send once per order: the first shipping update, whichever of fulfilled, pre-transit, or in transit comes first. Skip if we already messaged them about this order's shipment, unless that message covered only part of the order and this package has the rest.";
const SHIPPING_PROBLEM_SEND_POLICY_GUIDANCE =
  "Send now, once per shipment. Skip if it has since moved or arrived, a replacement or claim is under way, or we already told them it is late or stuck.";

const orderCreatedTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_CREATED,
  name: "Order Created",
  description: "Customer placed a new order",
  category: TriggerCategory.ORDER_LIFECYCLE,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The customer placed an order. It is confirmed and has not shipped. Exchange orders (marked isExchangeOrder) also fire this trigger; they come from a return, not a purchase.",
  sendPolicyGuidance:
    "Send now. Skip if it is an exchange order, it was placed more than 24 hours ago, the customer asked to cancel it or said it was a mistake, or the conversation already acknowledged this order.",
  processingPolicy: {
    buffer: { defaultBypass: true, supportsOverride: true },
    sentinel: { defaultBypass: true, supportsOverride: true },
  },
};

const orderFulfilledTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_FULFILLED,
  name: "Order Fulfilled",
  description: "Order has been fulfilled and shipped",
  category: TriggerCategory.ORDER_LIFECYCLE,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The merchant fulfilled all or part of the order, which usually means it was packed and a shipping label was created; the carrier may not have scanned it yet. An order shipped in several packages fires this once per package.",
  sendPolicyGuidance: FIRST_SHIPPING_UPDATE_SEND_POLICY_GUIDANCE,
};

const orderPreTransitTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_PRE_TRANSIT,
  name: "Pre-Transit",
  description:
    "A shipping label was created, but the carrier hasn't scanned the package yet",
  category: TriggerCategory.SHIPPING_UPDATES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "A shipping label was created, and the carrier has not picked up or scanned the package yet. Packages can stay in this state for a day or more.",
  sendPolicyGuidance: FIRST_SHIPPING_UPDATE_SEND_POLICY_GUIDANCE,
};

const orderInTransitTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_IN_TRANSIT,
  name: "In Transit",
  description: "Package is moving through the carrier network",
  category: TriggerCategory.SHIPPING_UPDATES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The carrier has scanned the package, and it is moving toward the customer.",
  sendPolicyGuidance: FIRST_SHIPPING_UPDATE_SEND_POLICY_GUIDANCE,
};

const orderOutForDeliveryTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_OUT_FOR_DELIVERY,
  name: "Out for Delivery",
  description: "Package is out for delivery today",
  category: TriggerCategory.SHIPPING_UPDATES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The package is on the delivery vehicle and expected to arrive today.",
  sendPolicyGuidance:
    "Send now, on the delivery day only. Skip if we messaged them about this order's shipment in the last 24 hours.",
};

const orderDeliveredTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_DELIVERED,
  name: "Order Delivered",
  description: "Package has been delivered",
  category: TriggerCategory.SHIPPING_UPDATES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The carrier reports the package delivered. For an order shipped in several packages, this may be one of them.",
  sendPolicyGuidance:
    "Send once per order. Skip if it was delivered more than 3 days ago, they reported it missing, or they have started a return or claim for this order.",
};

const orderAvailableForPickupTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_AVAILABLE_FOR_PICKUP,
  name: "Available for Pickup",
  description: "Order is ready for customer pickup",
  category: TriggerCategory.PICKUP,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The order is ready for the customer to collect at the store they chose at checkout. It will not be shipped.",
  sendPolicyGuidance:
    "Send now, once per order. Skip if they have already collected it.",
};

const orderAvailableForPickupCarrierTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_AVAILABLE_FOR_PICKUP_CARRIER,
  name: "Available for Pickup (Carrier)",
  description: "Order is ready for pickup at carrier location",
  category: TriggerCategory.PICKUP,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The package is waiting at a carrier location, such as a post office or access point, and the customer must collect it there. This often follows a missed delivery; uncollected packages go back to the merchant after the carrier's hold period.",
  sendPolicyGuidance:
    "Send now, once per package. Skip if they have already collected it.",
};

const orderStalledInTransitTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_STALLED_IN_TRANSIT,
  name: "Stalled in Transit",
  description: "Shipment appears to be stuck in transit",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The carrier has reported no movement for the package for longer than expected. It has not been delivered; it may be delayed, lost, or moving without scans.",
  sendPolicyGuidance: SHIPPING_PROBLEM_SEND_POLICY_GUIDANCE,
};

const orderDelayedTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_DELAYED,
  name: "Order Delayed",
  description:
    "The carrier pushed the estimated delivery date back by more than a week",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The carrier pushed the package's estimated delivery date back by more than a week. It is in transit and has not been delivered.",
  sendPolicyGuidance: SHIPPING_PROBLEM_SEND_POLICY_GUIDANCE,
};

const orderStalledInFulfillmentTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_STALLED_IN_FULFILLMENT,
  name: "Stalled in Fulfillment",
  description: "Order hasn't shipped within the expected fulfillment window",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The order has not shipped within the merchant's expected fulfillment window. This is normal for pre-order, backorder, and made-to-order items.",
  sendPolicyGuidance:
    "Send now, once per order. Skip if its items are pre-order, backorder, or made-to-order.",
};

const orderDeliveryFailureTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_DELIVERY_FAILURE,
  name: "Delivery Failure",
  description: "Carrier could not deliver the package",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The carrier could not deliver the package, often after several attempts or because of an address problem. It has not reached the customer and may be held for pickup or sent back.",
  sendPolicyGuidance:
    "Send now, once per shipment. Skip if it has since been delivered, collected, or redirected.",
};

const orderDeliveryAttemptedTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_DELIVERY_ATTEMPTED,
  name: "Delivery Attempted",
  description: "Carrier attempted delivery but was unsuccessful",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The carrier tried to deliver the package and could not, often because no one was available or access was blocked. Carriers usually leave a notice and try again, often the next business day.",
  sendPolicyGuidance:
    "Send now, at most once per shipment per day. Skip if it has since been delivered.",
};

const orderReturnToSenderTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_RETURN_TO_SENDER,
  name: "Return to Sender",
  description: "Package is being returned to the sender",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The carrier is sending the package back to the merchant, so it will not reach the customer as shipped. Causes include a bad address, a refused delivery, an uncollected package, or a cancellation after shipping.",
  sendPolicyGuidance:
    "Send now, once per shipment. Skip if the merchant has already reshipped the order.",
};

const orderShipmentCancelledTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_SHIPMENT_CANCELLED,
  name: "Shipment Cancelled",
  description: "A shipment or the whole order was cancelled",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "A shipment, or the whole order, was cancelled before delivery. Common causes: the customer asked to cancel, the merchant cancelled the order, or the merchant voided a shipping label to replace it, in which case the order still ships.",
  sendPolicyGuidance:
    "Wait 60 minutes, then decide. Send only if the order itself is now cancelled and the customer did not ask for that. If the order is still open, skip: the label was most likely replaced.",
};

const orderShipmentErrorTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_SHIPMENT_ERROR,
  name: "Shipment Error",
  description: "An error occurred with the shipment",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The carrier reported an error with the shipment, such as a label, address, or customs problem, so its delivery status is uncertain. The merchant or carrier usually has to fix it.",
  sendPolicyGuidance:
    "Wait up to 120 minutes, then decide. Send once per shipment if the error still stands; skip if the package has moved or arrived since.",
};

const orderArrivingEarlyTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_ARRIVING_EARLY,
  name: "Arriving Early",
  description: "Package is arriving earlier than expected",
  category: TriggerCategory.SHIPPING_UPDATES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance:
    "The carrier moved the package's estimated delivery date earlier, by more than 3 days. It has not arrived yet.",
  sendPolicyGuidance:
    "Send now. Skip if we messaged them about this order's shipment in the last 24 hours.",
};

// ---------------------------------------------------------------------------
// Aggregated lists — derived from category
// ---------------------------------------------------------------------------

export const ALL_TRIGGERS: TriggerDefinition[] = [
  // Pre-purchase
  checkoutAbandonedTrigger,
  cartAbandonedTrigger,
  browseAbandonedTrigger,
  emailSignupTrigger,
  smsSignupTrigger,
  priceDropTrigger,
  backInStockTrigger,
  emailSignupShopifyTrigger,
  bigcommerceCartAbandonedTrigger,
  bigcommerceCheckoutAbandonedTrigger,
  commentsoldCartAbandonedTrigger,
  commentsoldBrowseAbandonedTrigger,
  commentsoldCheckoutAbandonedTrigger,
  // Post-purchase
  refundReturnSubmittedTrigger,
  exchangeProcessedWithCreditTrigger,
  warrantyRegistrationTrigger,
  orderCreatedTrigger,
  orderFulfilledTrigger,
  orderPreTransitTrigger,
  orderInTransitTrigger,
  orderOutForDeliveryTrigger,
  orderDeliveredTrigger,
  orderAvailableForPickupTrigger,
  orderAvailableForPickupCarrierTrigger,
  orderStalledInTransitTrigger,
  orderDelayedTrigger,
  orderStalledInFulfillmentTrigger,
  orderDeliveryFailureTrigger,
  orderDeliveryAttemptedTrigger,
  orderReturnToSenderTrigger,
  orderShipmentCancelledTrigger,
  orderShipmentErrorTrigger,
  orderArrivingEarlyTrigger,
];

export const PRE_PURCHASE_TRIGGERS = ALL_TRIGGERS.filter((t) =>
  PRE_PURCHASE_CATEGORIES.includes(t.category),
);

export const POST_PURCHASE_TRIGGERS = ALL_TRIGGERS.filter((t) =>
  POST_PURCHASE_CATEGORIES.includes(t.category),
);

export const TRIGGERS_BY_CATEGORY: Record<
  TriggerCategory,
  TriggerDefinition[]
> = Object.fromEntries(
  Object.values(TriggerCategory).map((category) => [
    category,
    ALL_TRIGGERS.filter((t) => t.category === category),
  ]),
) as Record<TriggerCategory, TriggerDefinition[]>;

const triggersById = new Map<TriggerKey, TriggerDefinition>(
  ALL_TRIGGERS.map((trigger) => [trigger.id, trigger]),
);

export function getTriggerById(id: TriggerKey): TriggerDefinition | undefined {
  return triggersById.get(id);
}
